import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import app from 'flarum/forum/app';
import Api from '../utils/Api';
import { formatAmount, formatDelta, tierClassName } from '../utils/refundFormat';
// v0.19.20 (批 41 · 段②) — 「后端错误文案」改用**共用入口**
//   (本仓铁律「一个逻辑一个入口, 不许抄两遍」; 改前这个原语在本文件里抄了一份)。
import { errText } from '../utils/FormPrimitives';
// v0.19.22 (批 43) — 「审批档序号」改用**共用入口** (改前本文件与 RefundRequestModal 各抄了一份)
import { tierOrder } from '../utils/RefundTier';
import RefundResultModal from './RefundResultModal';

/**
 * v0.18.62 (辉哥 10/23 拍板) — **审批退卡申请** modal (设计稿 v4 · S4)
 *
 * 视觉基线: `public/assets/ziven-scheduler-refund-mockup.html` 的 `?scene=approve`
 *
 * ## ⛔ 数据来源 (一个数都不许前端自己算)
 *   · `系统核算`     = **预览端点**的 `system_amount`
 *     (⚠️ 卡上只下发 `refund_amount` = 最终申请额, ⛔ **系统核算额不在卡字段里**
 *      ⇒ 审批弹窗也必须调 `GET …/refund/preview` 拿)
 *   · `申请退款金额` = `card.refund_amount` (发起时**冻结**的那笔)
 *   · `退费原因`     = 预览端点的 `reason_label`
 *   · `审批档`       = 预览端点的 `approval_label` + `tier_order`
 *     (⛔ 前端**不建** tier → 文案 / tier → tier-N 映射表)
 *   · `备注是否必填` = `card.refund_requires_approver_note`
 *     (⚠️ 用**卡上冻结**的那个标志, ⛔ 不用预览端点重算的 ——
 *      审批义务是发起时定下的, 不该因为审批期间卡被继续消课而漂移)
 *
 * ## 驳回的二次确认 (辉哥 10/22 明确拍板)
 *   用**浏览器原生 `window.confirm()`** ——
 *   ⛔ **不**自建 Modal (辉哥实机试过 Flarum 原生 modal, 观感不好, 明确改口)
 *   ⛔ **不**包 `setTimeout` (会丢用户手势上下文, 移动端可能不弹)
 *   ⛔ `e.preventDefault()` 只保留同步部分
 */

/** 发起人角色 → 中文 (设计稿 S4 的「发起人 陆知遥 销售」) */
const ROLE_LABELS = {
  coach: '教练',
  sales: '销售',
  manager: '店长',
  student: '学员'
};
export default class RefundApproveModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    this.card = vnode.attrs.card || {};

    // 预览端点的响应 (系统核算额 + 审批档)
    this.preview = null;
    this.previewLoading = true;
    this.note = '';
    this.error = '';
    this.saving = false;
    this.loadPreview();
  }
  className() {
    return 'Modal--small Modal--fullscreen-padded ZS-RefundApproveModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-shield-halved",
      style: "color:var(--zs-purple-8b5cf6, #8b5cf6);margin-right:8px;"
    }), "\u5BA1\u6279\u9000\u5361\u7533\u8BF7");
  }

  // ─────────────────────────────────────────────────────────────────────
  // 数据
  // ─────────────────────────────────────────────────────────────────────

  /**
   * 拉预览 (只读)。
   *
   * ⚠️ `reason` 用**卡上冻结的** `refund_reason` ⇒ 拿回来的 `system_amount`
   *   与发起时是**同一套公式** (三套公式的核算额不同, 原因必须一致)。
   */
  async loadPreview() {
    this.previewLoading = true;
    m.redraw();
    try {
      const reason = this.card.refund_reason || 'institution';
      const r = await Api.refundPreview(this.card.id, reason);
      this.preview = r || null;
    } catch (e) {
      this.preview = null;
      this.error = errText(e, '退费预览失败');
    } finally {
      this.previewLoading = false;
      m.redraw();
    }
  }

  /** 后端错误的 `detail` 文案 (⛔ 不自己编文案)
   *  ⚠️ v0.19.20 (批 41 · 段②) — 实现**搬**进共用入口 `utils/FormPrimitives.js`
   *    (改前 `errText` 在本文件里抄了一份 ⇒ 本仓铁律「一个逻辑一个入口」)。 */

  /** 备注是否必填 (⛔ 用卡上**冻结**的标志) */
  noteRequired() {
    return this.card.refund_requires_approver_note === true;
  }

  /** 申请额 vs 系统核算额的差额 (null = 没数据 / 相等) */
  deltaInfo() {
    const p = this.preview;
    if (!p || p.system_amount === null || p.system_amount === undefined) return null;
    const sys = Number(p.system_amount);
    const applied = Number(this.card.refund_amount);
    if (!isFinite(sys) || !isFinite(applied)) return null;
    const diff = applied - sys;
    if (Math.abs(diff) < 0.005) return null;
    return {
      sys,
      applied,
      diff,
      over: diff > 0,
      under: diff < 0
    };
  }

  /** 发起人 (姓名 + 角色) —— ⛔ 后端直接下发, 前端**不再**拉全站人员名单 */
  requesterText() {
    const id = this.card.refund_requested_by;
    const name = this.card.refund_requested_by_name;
    const roleKey = this.card.refund_requested_by_role;
    if (!id) return {
      name: '—',
      role: ''
    };
    if (!name) return {
      name: '#' + id,
      role: ''
    };
    return {
      name: name,
      role: ROLE_LABELS[roleKey] || ''
    };
  }

  // ─────────────────────────────────────────────────────────────────────
  // 渲染
  // ─────────────────────────────────────────────────────────────────────

  content() {
    const card = this.card || {};
    const preview = this.preview;
    const delta = this.deltaInfo();
    const requester = this.requesterText();
    const studentName = card.student_display_name || '学员';
    const systemAmount = preview ? preview.system_amount : null;
    const requiresNote = this.noteRequired();
    return m('[', null, m("div", {
      className: "ZS-RefundModal-body"
    }, m("div", {
      className: "ZS-RefundModal-rows"
    }, m("div", {
      className: "rrow"
    }, m("span", {
      className: "rk"
    }, "\u7CFB\u7EDF\u6838\u7B97"), m("span", {
      className: "rv",
      style: "color:var(--zs-text-2-9ca3af, #9ca3af);font-variant-numeric:tabular-nums"
    }, systemAmount === null || systemAmount === undefined ? '—' : '¥' + formatAmount(systemAmount))), m("div", {
      className: "rrow"
    }, m("span", {
      className: "rk"
    }, "\u7533\u8BF7\u9000\u6B3E\u91D1\u989D"), m("span", {
      className: "rv money"
    }, "\xA5", formatAmount(card.refund_amount))), m("div", {
      className: "rrow"
    }, m("span", {
      className: "rk"
    }, "\u9000\u8D39\u539F\u56E0"), m("span", {
      className: "rv"
    }, preview ? preview.reason_label : '—')), m("div", {
      className: "rrow"
    }, m("span", {
      className: "rk"
    }, "\u53D1\u8D77\u4EBA"), m("span", {
      className: "rv"
    }, requester.name, requester.role ? m("span", {
      className: "sub"
    }, requester.role) : null)), m("div", {
      className: "rrow"
    }, m("span", {
      className: "rk"
    }, "\u53D1\u8D77\u65F6\u95F4"), m("span", {
      className: "rv"
    }, card.refund_at || '—')), m("div", {
      className: "rrow"
    }, m("span", {
      className: "rk"
    }, "\u5173\u8054\u5361"), m("span", {
      className: "rv"
    }, card.template_name || `学员卡 #${card.id}`, m("span", {
      className: "sub"
    }, studentName, " \xB7 \u5269\u4F59 ", card.remaining_lessons, " \u8BFE\u65F6")))), delta && m("div", {
      className: "ZS-RefundModal-deltarow"
    }, m("i", {
      className: "fas fa-circle-exclamation"
    }), m("span", null, "\u7533\u8BF7\u91D1\u989D\u6BD4\u7CFB\u7EDF\u6838\u7B97", delta.over ? '多' : '少', " ", m("b", null, "\xA5", formatDelta(delta.diff)), "\uFF0C \u5B9E\u9645\u6253\u6B3E\u6309", m("b", null, "\u7533\u8BF7\u9000\u6B3E\u91D1\u989D"), "\uFF0C\u5BA1\u6279\u6863\u6309", m("b", null, "\u7CFB\u7EDF\u6838\u7B97\u989D"), "\u5224\u5B9A\u3002")), m("div", {
      className: "ZS-RefundModal-tiernote"
    }, m("i", {
      className: "fas fa-scale-balanced"
    }), m("span", null, "\u5BA1\u6279\u6863\u6309", m("b", null, "\u300C\u7CFB\u7EDF\u6838\u7B97\u989D\u300D"), "\u5224\u5B9A\uFF0C\u624B\u586B\u91D1\u989D\u4E0D\u6539\u53D8\u5BA1\u6279\u6863", preview && preview.tier_hint ? m("span", null, "\uFF1B", preview.tier_hint, "\u3002") : m("span", null, "\u3002"))), m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: "fas fa-layer-group",
      style: "color:var(--zs-purple-8b5cf6, #8b5cf6)"
    }), " \u5BA1\u6279\u6863"), m("div", null, m("span", {
      className: 'ZS-RefundModal-tier ' + tierClassName(tierOrder(this.preview))
    }, preview ? preview.approval_label : '—')), m("div", {
      className: "helpText"
    }, "\u5BA1\u6279\u6863\u7531\u540E\u7AEF\u6309", m("b", null, "\u7CFB\u7EDF\u6838\u7B97\u989D"), "\u5224\u5B9A\u540E\u4E0B\u53D1\uFF1B\u524D\u7AEF\u53EA\u8BFB\u5C55\u793A\uFF0C\u4E0D\u5728\u524D\u7AEF\u5224\u6863\u3002")), m("div", {
      className: "ZS-RefundModal-hint warn"
    }, m("i", {
      className: "fas fa-triangle-exclamation"
    }), m("div", null, m("b", null, "\u9A73\u56DE\u662F\u4E0D\u53EF\u9006\u52A8\u4F5C"), "\uFF1A\u9A73\u56DE\u524D\u4F1A\u7528", m("b", null, "\u6D4F\u89C8\u5668\u539F\u751F\u786E\u8BA4\u6846"), "\u505A\u4E8C\u6B21\u786E\u8BA4\uFF0C\u786E\u8BA4\u540E\u624D\u63D0\u4EA4\u3002 \u9A73\u56DE\u540E\u8FD9\u5F20\u5361\u4FDD\u6301\u539F\u72B6\uFF0C\u53EF\u91CD\u65B0\u53D1\u8D77\u9000\u8D39\u7533\u8BF7\u3002")), m("div", {
      className: "Form-group"
    }, m("label", null, m("i", {
      className: "fas fa-file-lines",
      style: "color:var(--zs-brand-3b82f6, #3b82f6)"
    }), " \u5BA1\u6279\u5907\u6CE8\uFF08\u7EBF\u4E0B\u51ED\u636E\u8BF4\u660E\uFF09"), m("textarea", {
      className: 'FormControl' + (requiresNote ? ' is-error' : ''),
      rows: "3",
      placeholder: requiresNote ? '填写本次审批对应的线下凭据说明' : '填写本次审批对应的线下凭据说明（本档选填）',
      value: this.note,
      oninput: e => {
        this.note = e.target.value;
        m.redraw();
      }
    }), requiresNote && m("div", {
      className: "ZS-RefundModal-required"
    }, m("i", {
      className: "fas fa-circle-exclamation"
    }), m("span", null, "\u8BE5\u5BA1\u6279\u6863\u5FC5\u987B\u586B\u5199\u7EBF\u4E0B\u51ED\u636E\u8BF4\u660E\u540E\u624D\u80FD\u901A\u8FC7"))), this.error && m("div", {
      className: "ZS-RefundModal-error"
    }, this.error)), m("div", {
      className: "Modal-footer"
    }, m(Button, {
      className: "Button Button--reject",
      disabled: this.saving,
      onclick: e => this.onReject(e)
    }, "\u9A73\u56DE"), m(Button, {
      className: "Button Button--approve ZS-RefundApproveModal-submit",
      disabled: this.saving || !this.canApprove(),
      loading: this.saving,
      onclick: () => this.approve()
    }, "\u5BA1\u6279\u901A\u8FC7")));
  }

  /**
   * 「审批通过」能不能点。
   *
   * ⛔ 只有一件事会挡住: **备注必填档但备注为空** (后端会 422, 前端先拦一道)。
   */
  canApprove() {
    if (this.noteRequired() && this.note.trim() === '') return false;
    return true;
  }

  // ─────────────────────────────────────────────────────────────────────
  // 提交
  // ─────────────────────────────────────────────────────────────────────

  async approve() {
    if (!this.canApprove()) return;
    this.saving = true;
    this.error = '';
    m.redraw();
    try {
      const resp = await Api.approveRefund(this.card.id, {
        approver_note: this.note.trim()
      });
      this.saving = false;

      // 🔴 v0.18.76 (D5) — 审批通过 ⇒ **弹一张回执页** (设计稿 D5)
      //   改前: `this.hide()` ⇒ 弹窗直接消失, 审批人**看不到任何结果**
      //   (不知道审批有没有落库、卡有没有作废、打了多少钱)。
      //
      //   ⛔ **不要**先 `this.hide()` 再 `app.modal.show(...)` ——
      //     Flarum 2.0 的 `app.modal.show()` **默认就把整个弹窗栈替换掉**
      //     (keepStack 默认 false ⇒ `modalList = [this.modal]`), 本弹窗自动消失。
      //     而「先 hide() 再 show()」会**锁死全站弹窗**:
      //       hide() → ModalManager.animateHide() ⇒ `modalClosing = true`,
      //       并在**本弹窗的 DOM 元素**上挂 `transitionend` 监听;
      //       紧接着 show() 把本弹窗的 DOM 移除 ⇒ transitionend 永不触发
      //       ⇒ `state.close()` 永不执行 ⇒ `modalClosing` 永久停在 true
      //       ⇒ 之后**任何**弹窗的 hide() 都被闩锁挡住 ⇒ 取消 / × / Esc 全部失效。
      //     (同款说明见 RefundRequestModal.js L497-506 / CardDetailModal.js L100-110。)
      //
      //   ⛔ `variant: 'approve'` ⇒ 回执读**平铺的** `result.*` (审批端点的返回体);
      //     不传 variant 时默认走 `request` 变体 (读 `result.meta.*`) —— 两条路径不许混用。
      app.modal.show(RefundResultModal, {
        card: this.card,
        result: resp || {},
        variant: 'approve'
      });

      // 审批通过 ⇒ 列表里的卡状态变了 ⇒ 让上层刷新
      if (typeof this.attrs.onDone === 'function') this.attrs.onDone();
    } catch (e) {
      this.error = errText(e, '审批失败');
      this.saving = false;
      m.redraw();
    }
  }

  /**
   * 驳回 —— **浏览器原生二次确认** (辉哥 10/22 拍板)。
   *
   * ⚠️ `window.confirm()` 必须**同步**调 (⛔ 不许包 setTimeout ——
   *   那会丢用户手势上下文, 移动端可能根本不弹)。
   * ⚠️ `e.preventDefault()` 只保留同步部分。
   */
  onReject(e) {
    if (e && e.preventDefault) e.preventDefault();
    const ok = window.confirm('确认驳回这笔退费申请吗？驳回后卡片保持原状，可重新发起。');
    if (!ok) return;
    this.reject();
  }
  async reject() {
    this.saving = true;
    this.error = '';
    m.redraw();
    try {
      await Api.rejectRefund(this.card.id, {
        approver_note: this.note.trim()
      });
      this.saving = false;
      this.hide();
      if (typeof this.attrs.onDone === 'function') this.attrs.onDone();
    } catch (e) {
      this.error = errText(e, '驳回失败');
      this.saving = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/RefundApproveModal', RefundApproveModal);